import { useCallback, useEffect, useState } from "react";
import { LAST_SEEN_STORAGE_KEY } from "../config";
import { useWhatsNewIndex } from "./useWhatsNew";

const readLastSeen = (): number | null => {
  try {
    const raw = localStorage.getItem(LAST_SEEN_STORAGE_KEY);
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const writeLastSeen = (value: number): void => {
  try {
    localStorage.setItem(LAST_SEEN_STORAGE_KEY, String(value));
  } catch {
    // localStorage unavailable (private mode, quota); silently ignore
  }
};

export const useUnreadWhatsNew = () => {
  const { data: entries } = useWhatsNewIndex();
  const [lastSeenId, setLastSeenId] = useState<number | null>(readLastSeen);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== LAST_SEEN_STORAGE_KEY) return;
      const next = e.newValue === null ? null : Number(e.newValue);
      setLastSeenId(Number.isFinite(next as number) ? (next as number) : null);
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const latestId = entries?.[0]?.id ?? null;
  const hasUnread = Boolean(
    latestId !== null && (lastSeenId === null || latestId > lastSeenId)
  );
  const unreadCount = entries
    ? entries.filter((e) => lastSeenId === null || e.id > lastSeenId).length
    : 0;

  const markAllAsRead = useCallback(() => {
    if (latestId === null) return;
    writeLastSeen(latestId);
    setLastSeenId(latestId);
  }, [latestId]);

  return { hasUnread, unreadCount, latestId, lastSeenId, markAllAsRead };
};
